import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { WorkItem } from '@/types'

import Link from 'next/link'
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { getWorkBySlug, getWorkList } from '@/lib/work'
import PhoneFrame from '@/components/landing/phone-frame'

interface CaseStudyProps {
    item: WorkItem
    next: WorkItem | null
}

const metricFields = [
    { value: 'metric_1_value', label: 'metric_1_label' },
    { value: 'metric_2_value', label: 'metric_2_label' },
    { value: 'metric_3_value', label: 'metric_3_label' }
] as const

const CaseStudy: FC<CaseStudyProps> = ({ item, next }): ReactNode => {
    const metrics = metricFields.filter(field => item[field.value])

    return (
        <Layout>
            <Seo title={item.title} description={item.summary ?? undefined} />

            <article>
                <section id='case-study-hero' className='relative overflow-hidden border-b border-ink/10'>
                    <div className='pointer-events-none absolute -top-32 -left-32 size-[460px] rounded-full bg-signal/15 blur-3xl' aria-hidden='true' />

                    <div className='relative mx-auto max-w-7xl px-5 pt-10 pb-20 sm:px-8 lg:pt-14'>
                        <nav aria-label='Breadcrumb' className='font-mono text-xs uppercase tracking-[0.18em] text-ink/55'>
                            <ol className='flex flex-wrap items-center gap-2'>
                                <li>
                                    <Link href='/work' className='inline-flex items-center gap-1.5 hover:text-ink'>
                                        <ArrowLeft className='size-3.5' aria-hidden='true' />
                                        Work
                                    </Link>
                                </li>
                                <li aria-hidden='true'>/</li>
                                <li aria-current='page'>
                                    <span data-mdlfy-cms={`work:${item.id}:brand`}>{item.brand}</span>
                                </li>
                            </ol>
                        </nav>

                        <div className='mt-10 grid items-start gap-14 lg:grid-cols-[minmax(0,320px)_1fr] lg:gap-20'>
                            <div className='mx-auto w-full max-w-[300px] lg:sticky lg:top-24 lg:max-w-none'>
                                <PhoneFrame image={item.thumbnail} alt={item.thumbnail_alt} video={item.video} autoPlay>
                                    <p className='text-xs font-semibold opacity-90'>
                                        @<span data-mdlfy-cms={`work:${item.id}:brand`}>{item.brand}</span>
                                        <span className='ml-1.5 rounded bg-bone/20 px-1 py-px text-[0.6rem] font-medium'>Sponsored</span>
                                    </p>
                                    {item.hook && (
                                        <p className='mt-1.5 text-sm leading-snug font-semibold [text-shadow:0_1px_8px_rgba(0,0,0,0.5)]'>
                                            <span data-mdlfy-cms={`work:${item.id}:hook`}>{item.hook}</span>
                                        </p>
                                    )}
                                </PhoneFrame>
                            </div>

                            <div>
                                <p className='flex flex-wrap gap-2'>
                                    {item.category && (
                                        <span className='rounded-full border border-ink/15 px-3 py-1 font-mono text-[0.7rem] uppercase tracking-[0.15em] text-ink/70'>
                                            <span data-mdlfy-cms={`work:${item.id}:category`}>{item.category}</span>
                                        </span>
                                    )}
                                    {item.format && (
                                        <span className='rounded-full bg-ink px-3 py-1 font-mono text-[0.7rem] uppercase tracking-[0.15em] text-bone'>
                                            <span data-mdlfy-cms={`work:${item.id}:format`}>{item.format}</span>
                                        </span>
                                    )}
                                </p>

                                <h1 className='mt-6 font-heading text-[clamp(2.75rem,6vw,5.25rem)] leading-[0.95] tracking-tight text-ink'>
                                    <span data-mdlfy-cms={`work:${item.id}:title`}>{item.title}</span>
                                </h1>

                                {item.summary && (
                                    <p className='mt-6 max-w-2xl text-xl leading-relaxed text-ink/70'>
                                        <span data-mdlfy-cms={`work:${item.id}:summary`}>{item.summary}</span>
                                    </p>
                                )}

                                {metrics.length > 0 && (
                                    <dl className='mt-12 grid gap-px overflow-hidden rounded-3xl border border-ink/10 bg-ink/10 sm:grid-cols-3'>
                                        {metrics.map(field => (
                                            <div key={field.value} className='bg-card p-6'>
                                                <dt className='text-sm text-ink/60'>
                                                    <span data-mdlfy-cms={`work:${item.id}:${field.label}`}>{item[field.label]}</span>
                                                </dt>
                                                <dd className='mt-2 font-heading text-5xl leading-none tracking-tight text-ink'>
                                                    <span data-mdlfy-cms={`work:${item.id}:${field.value}`}>{item[field.value]}</span>
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                )}

                                {item.hook && (
                                    <figure className='mt-12 rounded-3xl bg-ink p-8 text-bone sm:p-10'>
                                        <figcaption className='font-mono text-xs uppercase tracking-[0.2em] text-signal'>
                                            The hook
                                        </figcaption>
                                        <blockquote className='mt-4 font-heading text-3xl leading-tight sm:text-4xl'>
                                            <span data-mdlfy-cms={`work:${item.id}:hook`}>{item.hook}</span>
                                        </blockquote>
                                    </figure>
                                )}

                                <div className='mt-16 grid gap-12 md:grid-cols-2'>
                                    {item.challenge && (
                                        <section id='the-brief'>
                                            <h2 className='font-mono text-xs uppercase tracking-[0.2em] text-ink/55'>
                                                The brief
                                            </h2>
                                            <p className='mt-4 text-lg leading-relaxed whitespace-pre-line text-ink/80'>
                                                <span data-mdlfy-cms={`work:${item.id}:challenge`}>{item.challenge}</span>
                                            </p>
                                        </section>
                                    )}

                                    {item.approach && (
                                        <section id='what-we-made'>
                                            <h2 className='font-mono text-xs uppercase tracking-[0.2em] text-ink/55'>
                                                What we made
                                            </h2>
                                            <p className='mt-4 text-lg leading-relaxed whitespace-pre-line text-ink/80'>
                                                <span data-mdlfy-cms={`work:${item.id}:approach`}>{item.approach}</span>
                                            </p>
                                        </section>
                                    )}
                                </div>

                                {item.deliverables.length > 0 && (
                                    <section id='deliverables' className='mt-16 border-t border-ink/10 pt-10'>
                                        <h2 className='font-mono text-xs uppercase tracking-[0.2em] text-ink/55'>
                                            Delivered
                                        </h2>
                                        <ul className='mt-5 grid gap-3 sm:grid-cols-2'>
                                            {item.deliverables.map(deliverable => (
                                                <li key={deliverable} className='flex items-center gap-3 rounded-2xl border border-ink/10 bg-card px-4 py-3.5 text-ink'>
                                                    <span className='inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-signal text-ink' aria-hidden='true'>
                                                        <Check className='size-3' />
                                                    </span>
                                                    {deliverable}
                                                </li>
                                            ))}
                                        </ul>
                                    </section>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                <section id='next-steps' className='py-20'>
                    <div className='mx-auto grid max-w-7xl gap-6 px-5 sm:px-8 md:grid-cols-2'>
                        {next && (
                            <Link
                                href={`/work/${next.slug}`}
                                className='group flex flex-col justify-between gap-10 rounded-3xl border border-ink/10 bg-card p-8 transition-colors hover:border-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-signal sm:p-10'
                            >
                                <span className='font-mono text-xs uppercase tracking-[0.2em] text-ink/55'>
                                    Next case study
                                </span>
                                <span>
                                    <span className='block text-sm text-ink/60'>
                                        <span data-mdlfy-cms={`work:${next.id}:brand`}>{next.brand}</span>
                                    </span>
                                    <span className='mt-2 flex items-end justify-between gap-6'>
                                        <span className='font-heading text-3xl leading-tight text-ink sm:text-4xl'>
                                            <span data-mdlfy-cms={`work:${next.id}:title`}>{next.title}</span>
                                        </span>
                                        <ArrowRight className='size-6 shrink-0 transition-transform group-hover:translate-x-1' aria-hidden='true' />
                                    </span>
                                </span>
                            </Link>
                        )}

                        <div className='flex flex-col justify-between gap-10 rounded-3xl bg-signal p-8 sm:p-10'>
                            <span className='font-mono text-xs uppercase tracking-[0.2em] text-ink/70'>
                                Your turn
                            </span>
                            <div>
                                <p className='font-heading text-3xl leading-tight text-ink sm:text-4xl'>
                                    Want ads like this for your brand?
                                </p>
                                <Link
                                    href='/#contact'
                                    className='mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-sm font-semibold text-bone transition-transform hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ink'
                                >
                                    Start a brief
                                    <ArrowUpRight className='size-4' aria-hidden='true' />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </article>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const work = await getWorkList()
        return { paths: work.map(item => ({ params: { slug: item.slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Could not list case studies:', error instanceof Error ? error.message : error)
        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<CaseStudyProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    const item = await getWorkBySlug(slug)

    if (!item) {
        return { notFound: true, revalidate: 60 }
    }

    let next: WorkItem | null = null

    try {
        const all = await getWorkList()
        const index = all.findIndex(entry => entry.id === item.id)
        if (all.length > 1 && index !== -1) {
            next = all[(index + 1) % all.length]
        }
    }
    catch (error) {
        console.error('Could not load the next case study:', error instanceof Error ? error.message : error)
    }

    return { props: { item, next }, revalidate: 60 }
}

export default CaseStudy
